import { makeAutoObservable } from "mobx";
import { RemoteData } from "src/common/RemoteData";
import { ZUid } from "src/types/zUid";
import { Sheet2Store } from "src/components/Sheet2/Sheet2Store";
import { msgQueueStore } from "src/components/MsgQueue";
import { cvtError } from "src/common/onError";
import { ZSample2, ZSample2Row } from "../ZSample2";
import {
  loadSample2,
  updateSample2POMs,
  updateSample2Row,
} from "../apiSample2";
import { Sample2Column } from "./Sample2Table/Sample2Column";
import { buildSample2Columns } from "./Sample2Table/buildSample2Columns";
import { canUpdateRow } from "./sample2utils";

export const sample2ViewStore = makeAutoObservable({
  sheetStore: new Sheet2Store(),
  async init(entityId: ZUid = "") {
    this.setObjectId(entityId);
    this.selected.clear();
    try {
      this.setData({ status: "wait" });
      const result: ZSample2 = await loadSample2(entityId);
      this.setData({ status: "ready", result });
    } catch (error) {
      this.setData({ status: "error", error: cvtError(error) });
    }
  },
  objectId: "" as ZUid,
  setObjectId(id: ZUid) {
    this.objectId = id;
  },
  data: { status: "none" } as RemoteData<ZSample2>,
  setData(newData: RemoteData<ZSample2>) {
    this.data = newData;
    this.validateSelected();
  },

  get isReadOnly(): boolean {
    const { data } = this;
    if (data.status !== "ready") return true;
    return !data.result.permissions.includes("UPDATE");
  },

  get columns(): Sample2Column[] {
    const { sheetStore, selected, isReadOnly } = this;
    const onChange = async (
      newRow: ZSample2Row,
      field: "actualValue" | "newValue",
    ) => {
      const result = await updateSample2Row(newRow, field);
      return () => {
        const { data } = this;
        if (data.status === "ready") {
          const rows = data.result.rows.map((row) =>
            row.id === result.id ? result : row,
          );
          this.setData({
            status: "ready",
            result: {
              ...data.result,
              rows,
            },
          });
        }
      };
    };
    const onChangeSelect = (id: ZUid) => {
      this.changeSelect(id);
    };
    return buildSample2Columns({
      sheetStore,
      onChange,
      selected,
      onChangeSelect,
      isReadOnly,
      rowsSrc: this,
    });
  },

  get rows(): ZSample2Row[] {
    const { data } = this;
    return data.status === "ready" ? data.result.rows : [];
  },

  selected: new Set<ZUid>(),
  changeSelect(rowId: ZUid | "allPossible" | "empty") {
    if (rowId === "empty") {
      this.selected.clear();
    } else if (rowId === "allPossible") {
      const ids: ZUid[] = this.rows.filter(canUpdateRow).map(({ id }) => id);
      this.selected = new Set(ids);
    } else if (this.selected.has(rowId)) {
      this.selected.delete(rowId);
    } else {
      this.selected.add(rowId);
    }
  },
  validateSelected() {
    this.rows.forEach((row) => {
      if (!canUpdateRow(row)) this.selected.delete(row.id);
    });
  },

  get canSelectAll(): boolean {
    const { selected } = this;
    return !!this.rows.find(
      (row) => canUpdateRow(row) && !selected.has(row.id),
    );
  },
  get canSelectEmpty(): boolean {
    return this.selected.size > 0;
  },

  mode: null as Mode,
  setMode(newMode: Mode) {
    this.mode = newMode;
  },

  // слегка костыльный механизм для перерисовки компонента
  // это понадобилось из-за того что когда поверх таблицы рисуется модалка, то ячейки удаляются в sheetStore.postUpdate()
  // а чтобы они появились, нужно вызвать перерисовку
  // обычно это происходит автоматически. но в даном случае оказалось что нет.
  refresh() {
    const { data } = this;
    if (data.status === "ready") {
      const { result } = data;
      this.setData({ status: "wait" });
      setTimeout(() => {
        this.setData({ status: "ready", result });
      }, 10);
    }
  },

  isImageVisible: false,
  setImageVisible(value: boolean) {
    this.isImageVisible = value;
  },

  async doUpdate() {
    this.setMode("doUpdatePOMs");
    try {
      await updateSample2POMs(Array.from(this.selected));
      this.changeSelect("empty");
      this.setMode(null);
      msgQueueStore.add({ type: "success", message: "Обновление выполнено" });
    } catch (e) {
      this.setMode("startUpdatePOMs");
      throw e;
    }
  },
});

type Mode = "doUpdatePOMs" | "startUpdatePOMs" | null;
